/**
 * The kanban's live bars (design board "Sessions kanban"): one per shared project someone is working in right now,
 * "store-locator is live: Luca typing · Flow chat for Luca writing · Sara on the preview", with Join (open the project)
 * and Watch (be told when someone starts editing there; utils/watchModel.js). From the page's shared `/api/v1/live`
 * poll; nothing when nobody is around.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Bell, BellRinging } from '@phosphor-icons/react';
import useLiveNow from '../../hooks/useLiveNow';
import { liveSummary } from '../../utils/liveModel';
import { liveBarParts, setWatched, watchedProjects } from '../../utils/watchModel';
import './Workspace.css';

export function useWatched() {
  const [watched, setList] = useState(watchedProjects);
  useEffect(() => {
    const onChange = () => setList(watchedProjects());
    window.addEventListener('flow:watchChanged', onChange);
    return () => window.removeEventListener('flow:watchChanged', onChange);
  }, []);
  return watched;
}

export default function KanbanLiveBar({ onJoin, you = null }) {
  const { projects } = liveSummary(useLiveNow());
  const watched = useWatched();
  if (projects.length === 0) { return null; }
  const toggleWatch = (projectId) => {
    const on = !watched.includes(projectId);
    setWatched(projectId, on);
    if (on && typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  };
  return (
    <div className="kanban-live-bars" data-testid="kanban-live-bars">
      {projects.map((project) => {
        const on = watched.includes(project.projectId);
        return (
          <div className="kanban-live-bar" data-testid="kanban-live-bar" key={project.projectId} role="status">
            <span aria-hidden className="flow-sidebar-live-dot" />
            <span className="kanban-live-text">
              <strong>{project.name || project.projectId} is live:</strong>{' '}
              {liveBarParts(project, you).join(' · ')}
            </span>
            <button className="in-project-btn" onClick={() => toggleWatch(project.projectId)} aria-pressed={on} title={on ? 'Stop telling me when someone starts editing here' : 'Tell me when someone starts editing here'} type="button">
              {on ? <BellRinging aria-hidden size={14} /> : <Bell aria-hidden size={14} />} {on ? 'Watching' : 'Watch'}
            </button>
            <button className="in-project-btn is-primary" onClick={() => onJoin(project.projectId)} type="button">Join</button>
          </div>
        );
      })}
    </div>
  );
}

KanbanLiveBar.propTypes = {
  onJoin: PropTypes.func.isRequired,
  you: PropTypes.object,
};
